import closeIcon from '@disy/cadenza-icons/close.svg';
import errorIcon from '@disy/cadenza-icons/error.svg';
import infoIcon from '@disy/cadenza-icons/info.svg';
import moreIcon from '@disy/cadenza-icons/more.svg';
import lockIcon from '@disy/cadenza-icons/lock.svg';
import h from 'hyperscript';

import { hierarchyLevelDetails } from 'ui/attribute-label/attribute-formatting-utils';
import { createIconButton } from 'ui/button/button';
import type { Content } from 'ui/content';
import type { MenuContent } from 'ui/menu/menu';
import { setupMenuButton } from 'ui/menu/menu';
import { setupPopupButton } from 'ui/popup/popup';
import { createAttributeSummary } from 'ui/resource-summary/attribute-summary';
import { createToggletip } from 'ui/toggletip/toggletip';

import { isModified } from 'cadenza/attribute-util';
import { AttributeStatus } from 'cadenza/repository/model/attribute-status';
import { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { icon } from 'cadenza/utils/icon/icon';
import { dataTypeIconElements } from 'cadenza/workbook/attribute-element/data-type-icon';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import { markTokensInElement } from 'cadenza/utils/mark-substring';

import './attribute-tile.css';
import styles from './attribute-tile.module.css';
import i18n from './attribute-tile.properties';

export const COMPONENT_NAME = 'd-attribute-tile';

type RemovableType = boolean | 'SURFACE';

type Variant = 'compact';

interface AttributeTileContract {
  disabled: boolean;
  /**
   * enables compact mode
   */
  variant: Variant | undefined;
  /**
   * hides the main buttons when not hovered
   */
  hoverContext: boolean;
  /**
   * overwrites the internal invalid behaviour of the component
   *
   * | invalid | Attribute.status != 'OK' ‖ .is-invalid set | error message shown |
   * |---------|--------------------------‖-----------------|---------------------|
   * | falsy   | no                       ‖ no              | no                  |
   * | falsy   | yes                      ‖ yes             | yes                 |
   * | true    | yes/no                   ‖ yes             | no                  |
   * | string  | yes/no                   ‖ yes             | yes                 |
   */
  invalid: boolean | string;
  /**
   * enables the "more actions" button and assigns a menu to it, those contents are delivered here
   */
  menu: MenuContent | undefined;
  /**
   * hide the details panel
   */
  noDetails: boolean;
  /**
   * hides the link to the management center even if all information is available
   */
  noLinkToManagementCenter: boolean;
  /**
   * hide the attribute summary
   */
  noSummary: boolean;
  /**
   * if set to true, the attribute is removable. The corresponding action is added as an
   * action button. If `menu` is set, the action will be added as the last menu item instead.
   * To force the action to be displayed as an action button on the surface, set it to 'SURFACE'.
   */
  removable: RemovableType;
  /** If set, it will display a lock on the attribute tile and hide all on hoover buttons except the info button */
  locked: boolean;
  styleClass?: string;
}

export type AttributeTileOptions = Partial<AttributeTileContract>;

export function attributeTile(
  attribute: AttributeTileAttribute,
  options: AttributeTileOptions = {},
  details?: Content,
) {
  // function properties must be assigned manually since hyperscript assumes they're event listeners
  const { menu, styleClass, ...props } = options;
  const attributeTileElement = h<AttributeTile>('d-attribute-tile', { attribute, ...props }, details);
  // needs to be added because we want use querySelector on the component without it being connected to the DOM yet.
  attributeTileElement.classList.add('d-attribute-tile');
  if (styleClass) {
    attributeTileElement.classList.add(styleClass);
  }
  attributeTileElement.menu = menu;
  return attributeTileElement;
}

export const LOCK_ICON = icon(lockIcon, { variant: 'light', size: 'xs', styleClass: 'lock-icon' });

export class AttributeTile extends HTMLElement implements AttributeTileContract {
  static readonly observedAttributes = [
    'no-attribute-summary',
    'no-details',
    'no-link-to-management-center',
    'removable',
    'disabled',
  ] as const;

  override shadowRoot!: ShadowRoot;

  #attribute?: Readonly<AttributeTileAttribute>;
  #menu?: MenuContent;
  #invalid: string | boolean = false;
  #markTokens?: string[];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.adoptedStyleSheets = [styles];
  }

  connectedCallback() {
    this.classList.add('d-attribute-tile');
    this.#render();
  }

  // Needed for drag-drop
  override cloneNode(): AttributeTile {
    const clone = attributeTile(this.attribute, this);
    clone.className = this.className;
    return clone;
  }

  // BEGIN RENDERING

  // should always be called through #rerender() to avoid unnecessary updates
  #render() {
    if (!document.contains(this)) {
      return;
    }
    const attribute = this.attribute;
    this.shadowRoot.innerHTML = '';
    this.dataset.id = attribute.id;
    this.dataset.objecttypeId = attribute.objecttypeId;
    this.dataset.attributeType = attribute.type;
    this.dataset.attributeCategory = attribute.category;
    this.classList.toggle('is-modified', isModified(attribute));
    this.classList.toggle('is-invalid', Boolean(this.invalid));
    this.shadowRoot.append(
      ...[
        h(
          '.main',
          {
            part: 'attribute-tile-main',
          },
          h('.main-label', [
            ...dataTypeIconElements(attribute),
            markTokensInElement(h('.attribute-name.ellipsis', attribute.printName), this.#markTokens),
          ]),
          h('.main-buttons', [
            this.#renderInfoButton(),
            this.#renderMenu(),
            this.#renderRemoveButton(),
            this.#renderErrorButton(),
            LOCK_ICON.cloneNode(true),
          ]),
        ),
        h('.details.ellipsis', this.#renderDetails()),
        h('slot.additional-details'),
      ],
    );

    this.#markTokens = undefined;
  }

  #renderInfoButton() {
    if (this.noSummary) {
      return;
    }
    if (this.menu && !this.locked) {
      return;
    }
    if (this.#hasErrorMessage()) {
      return;
    }
    const button = createIconButton(icon(infoIcon), i18n('information'), {
      size: 'xs',
      variant: 'borderless',
      tabIndex: -1, // remove from tab order
      styleClass: [`${COMPONENT_NAME}--info-button`, ...(this.hoverContext ? ['is-hover-displayed'] : [])],
    });
    setupPopupButton(button, () => this.#renderAttributeSummary(), {
      placement: 'bottom',
      styleClass: ['d-popup-info', 'd-toggletip'],
    });
    return button;
  }

  #hasErrorMessage(value = this.invalid): value is string {
    return typeof value === 'string' && value !== '';
  }

  #renderErrorButton() {
    const invalid = this.invalid;
    if (!this.#hasErrorMessage(invalid)) {
      return;
    }
    return createToggletip({
      icon: icon(errorIcon, { styleClass: 'd-attribute-tile--error-tooltip-button' }),
      toggletipContent: () => h('d-alert.d-alert-error', invalid),
      toggletipStyleClass: 'd-popup-info',
      styleClass: ['button-error'],
    });
  }

  #renderRemoveButton() {
    if (this.removable === false) {
      return;
    }
    if (this.removable !== 'SURFACE' && this.menu && !this.#hasErrorMessage()) {
      return;
    }
    return createIconButton(icon(closeIcon), i18n('remove'), {
      variant: 'borderless',
      size: 'xs',
      onclick: () => this.#onRemove(),
      styleClass: ['remove', ...(this.hoverContext ? ['is-hover-displayed'] : [])],
    });
  }

  #renderMenu() {
    const menu = this.#menu;
    if (!menu) {
      return;
    }
    if (this.#hasErrorMessage()) {
      return;
    }
    const menuButton = createIconButton(icon(moreIcon), i18n('furtherActions'), {
      variant: 'borderless',
      size: 'xs',
      styleClass: this.hoverContext ? ['is-hover-displayed'] : [],
    });
    setupMenuButton(
      menuButton,
      () => {
        const attributeSummary = this.noSummary ? undefined : this.#renderAttributeSummary();
        const removeAction = { label: i18n('remove'), execute: () => this.#onRemove() };
        const furtherActions = this.removable && this.removable !== 'SURFACE' ? ['---', removeAction] : [];
        return Promise.resolve(menu()).then((items) => [attributeSummary, '---', ...items, ...furtherActions]);
      },
      { styleClass: 'd-popup-info' },
    );
    return menuButton;
  }

  #renderAttributeSummary() {
    return createAttributeSummary(this.attribute, {
      withDescription: true,
      withManagementLink: !this.noLinkToManagementCenter,
    });
  }

  #renderDetails() {
    if (this.noDetails) {
      return;
    }
    const attribute = this.attribute;
    const measureAggregation = attribute.measureAggregation;
    if (measureAggregation) {
      return MeasureAggregation.i18n(measureAggregation);
    }
    if (attribute.hierarchyType) {
      return hierarchyLevelDetails(attribute);
    }
  }

  // HTML-Attributes

  attributeChangedCallback(
    name: (typeof AttributeTile.observedAttributes)[number],
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (oldValue === newValue) {
      return;
    }
    this.#render();
  }

  // properties implementing the Options interface

  get attribute() {
    const attribute = this.#attribute;
    assertNonNullable(attribute);
    return attribute;
  }

  set attribute(value) {
    this.#attribute = value;
    this.#render();
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    this.toggleAttribute('disabled', value);

    const removeButton = this.shadowRoot.querySelector<HTMLButtonElement>('.remove');
    if (removeButton) {
      removeButton.disabled = value;
    }
  }

  get variant() {
    return this.classList.contains('d-attribute-tile-compact') ? 'compact' : undefined;
  }

  set variant(value) {
    this.classList.toggle('d-attribute-tile-compact', value === 'compact');
  }

  get hoverContext() {
    return this.classList.contains('d-hover-context');
  }

  set hoverContext(value) {
    this.classList.toggle('d-hover-context', value);
  }

  get invalid() {
    if (this.#invalid) {
      return this.#invalid;
    }
    const status = this.attribute.status;
    return status !== 'OK' && AttributeStatus.i18n(status);
  }

  set invalid(value) {
    this.#invalid = value;
    this.#render();
  }

  get menu() {
    return this.#menu;
  }

  set menu(value) {
    this.#menu = value;
    this.#render();
  }

  get noSummary() {
    return this.hasAttribute('no-attribute-summary');
  }

  set noSummary(value) {
    this.toggleAttribute('no-attribute-summary', value);
  }

  get noDetails() {
    return this.hasAttribute('no-details');
  }

  set noDetails(value) {
    this.toggleAttribute('no-details', value);
  }

  get noLinkToManagementCenter() {
    return this.hasAttribute('no-link-to-management-center');
  }

  set noLinkToManagementCenter(value) {
    this.toggleAttribute('no-link-to-management-center', value);
  }

  get removable() {
    if (this.hasAttribute('removable')) {
      const attributeValue = this.getAttribute('removable');
      return attributeValue === 'SURFACE' ? attributeValue : true;
    }
    return false;
  }

  set removable(value) {
    if (value === 'SURFACE') {
      this.setAttribute('removable', value);
    } else {
      this.toggleAttribute('removable', value);
    }
  }

  // Event Dispatching

  #onRemove() {
    const event = new CustomEvent('remove', { cancelable: true, bubbles: true });
    if (this.dispatchEvent(event)) {
      this.remove();
    }
  }

  mark(tokens?: string[]) {
    const name = this.shadowRoot.querySelector<HTMLElement>('.attribute-name');
    if (name) {
      markTokensInElement(name, tokens);
    } else {
      // Not connected to the DOM.
      this.#markTokens = tokens;
    }
  }

  set locked(locked: boolean) {
    if (locked) {
      this.setAttribute('locked', String(locked));
    } else {
      this.removeAttribute('locked');
    }
    this.#render();
  }

  get locked() {
    return Boolean(this.getAttribute('locked'));
  }
}

customElements.define(COMPONENT_NAME, AttributeTile);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: AttributeTile;
  }
}
